import { DownloadIcon } from '@solar-icons/react/linear/download'
import { CodeFileIcon } from '@solar-icons/react/linear/code-file'
import { DatabaseIcon } from '@solar-icons/react/linear/database'
import type { QueryResultSet } from '@/lib/api/models'
import { Badge, Button, CopyButton } from '@/components/ui'
import { csvFilename, toCsv, toJson, toTsv } from '@/lib/format/csv'
import { downloadTextFile } from '@/lib/utils/clipboard'
import { formatCount } from '@/lib/format/cells'
import { useLocale } from '@/lib/hooks/useLocale'

export interface ResultToolbarProps {
  result: QueryResultSet
  /** Used to name exported files. */
  query: string
}

export function ResultToolbar({ result, query }: ResultToolbarProps) {
  const locale = useLocale()

  if (!result.columns.length) return null

  const rowLabel = `${formatCount(result.rowCount, locale)} ${result.rowCount === 1 ? 'row' : 'rows'}`
  const colLabel = `${result.columns.length} ${result.columns.length === 1 ? 'col' : 'cols'}`

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Badge icon={<DatabaseIcon size={12} strokeWidth={2} />}>{rowLabel}</Badge>
      <Badge>{colLabel}</Badge>

      {result.truncated && (
        <Badge
          tone="warning"
          title={
            result.note ??
            'The backend capped this response. Narrow the question to see the rest.'
          }
        >
          showing {formatCount(result.displayedRowCount, locale)}
        </Badge>
      )}

      <div className="ml-auto flex items-center gap-1">
        <CopyButton
          value={() => toTsv(result)}
          label="Copy table"
          copiedLabel="Copied"
        />
        <Button
          variant="ghost"
          size="sm"
          icon={<DownloadIcon size={14} strokeWidth={1.75} />}
          title="Download the displayed rows as CSV"
          onClick={() =>
            downloadTextFile(csvFilename(query), toCsv(result), 'text/csv;charset=utf-8')
          }
        >
          CSV
        </Button>
        <Button
          variant="ghost"
          size="sm"
          icon={<CodeFileIcon size={14} strokeWidth={1.75} />}
          title="Download the displayed rows as JSON"
          onClick={() =>
            downloadTextFile(
              csvFilename(query).replace(/\.csv$/, '.json'),
              toJson(result),
              'application/json',
            )
          }
        >
          JSON
        </Button>
      </div>
    </div>
  )
}
